import * as ActionType from './constants';

export type OrganizationAction = {
  type: string,
  payload: {} | string,
};

export const clearOrganizationMessages = (): OrganizationAction => ({
  type: ActionType.CLEAR_ORGANIZATION_MESSAGES,
  payload: null,
});

export const loadOrganizationDetails = (): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_DETAILS,
  payload: null,
});

export const loadOrganizationDetailsSuccess = (organizationDetails): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_DETAILS_SUCCESS,
  payload: organizationDetails,
});

export const loadOrganizationDetailsFailed = (error: string): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_DETAILS_FAILED,
  payload: error,
});

export const loadOrganizationDashboardData = (): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_DASHBOARD_DATA,
  payload: null,
});

export const loadOrganizationDashboardDataSuccess = (
  isOwner,
  vpoTotalImplementation,
  vpoTotalForecast,
  vpoTotalProposition,
  totalClients,
  totalValueProposition,
  totalPortifolioAmount,
  totalFundamentals,
  valuePropositionSummary,
  usersByPlan?: {}
): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_DASHBOARD_DATA_SUCCESS,
  payload: {
    isOwner,
    vpoTotalImplementation,
    vpoTotalForecast,
    vpoTotalProposition,
    totalClients,
    totalValueProposition,
    totalPortifolioAmount,
    totalFundamentals,
    valuePropositionSummary,
    usersByPlan: usersByPlan && typeof usersByPlan === 'object' ? usersByPlan : {},
  },
});

export const loadOrganizationDashboardDataFailed = (error: string): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_DASHBOARD_DATA_FAILED,
  payload: error,
});

export const editOrganizationDetails = (organizationDetails): OrganizationAction => ({
  type: ActionType.EDIT_ORGANIZATION_DETAILS,
  payload: { organizationDetails },
});

export const editOrganizationDetailsSuccess = (
  newOrganizationDetails,
  successMessage
): OrganizationAction => ({
  type: ActionType.EDIT_ORGANIZATION_DETAILS_SUCCESS,
  payload: { newOrganizationDetails, successMessage },
});

export const editOrganizationDetailsFailed = (errorMessage): OrganizationAction => ({
  type: ActionType.EDIT_ORGANIZATION_DETAILS_FAILED,
  payload: errorMessage,
});

export const createOrganization = (organizationDetails): OrganizationAction => ({
  type: ActionType.CREATE_ORGANIZATION,
  payload: { organizationDetails },
});

export const createOrganizationSuccess = (
  newToken,
  successMessage,
  organizationId,
  showConfirmation = false,
  transferredVPsCount = 0,
  transferredClientsCount = 0
): OrganizationAction => ({
  type: ActionType.CREATE_ORGANIZATION_SUCCESS,
  payload: { 
    newUserToken: newToken, 
    successMessage, 
    organizationId,
    showConfirmation,
    transferredVPsCount,
    transferredClientsCount,
  },
});

export const createOrganizationFailed = (errorMessage): OrganizationAction => ({
  type: ActionType.CREATE_ORGANIZATION_FAILED,
  payload: errorMessage,
});

export const loadOrganizationClients = (): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_CLIENTS,
  payload: null,
});

export const loadOrganizationClientsSuccess = (organizationListClients): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_CLIENTS_SUCCESS,
  payload: organizationListClients,
});

export const loadOrganizationClientsFailed = (error: string): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_CLIENTS_FAILED,
  payload: error,
});

export const loadOrganizationMainClient = (mainClientId): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_MAIN_CLIENT,
  payload: { mainClientId },
});

export const loadOrganizationMainClientSuccess = (mainClientProfile): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_MAIN_CLIENT_SUCCESS,
  payload: { mainClientProfile },
});

export const loadOrganizationMainClientFailed = (error: string): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_MAIN_CLIENT_FAILED,
  payload: error,
});

export const saveOrganizationMainClient = (
  id,
  name,
  city,
  state,
  country,
  codeId,
  groupId,
  contactEmail,
  contactName,
  logo
): OrganizationAction => ({
  type: ActionType.SAVE_ORGANIZATION_MAIN_CLIENT,
  payload: {
    id,
    name,
    city,
    state,
    country,
    codeId,
    groupId,
    contactEmail,
    contactName,
    logo,
  },
});

export const saveOrganizationMainClientSuccess = (mainClient): OrganizationAction => ({
  type: ActionType.SAVE_ORGANIZATION_MAIN_CLIENT_SUCCESS,
  payload: mainClient,
});

export const saveOrganizationMainClientFailed = (error: string): OrganizationAction => ({
  type: ActionType.SAVE_ORGANIZATION_MAIN_CLIENT_FAILED,
  payload: error,
});

export const loadOrganizationUsers = (): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_USERS,
  payload: null,
});

export const loadOrganizationUsersSuccess = (organizationListUsers): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_USERS_SUCCESS,
  payload: organizationListUsers,
});

export const loadOrganizationUsersFailed = (error: string): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_USERS_FAILED,
  payload: error,
});

export const inviteUserToOrganization = (userEmail): OrganizationAction => ({
  type: ActionType.INVITE_USER_TO_ORGANIZATION,
  payload: { userEmail },
});

export const inviteUserToOrganizationSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.INVITE_USER_TO_ORGANIZATION_SUCCESS,
  payload: successMessage,
});

export const inviteUserToOrganizationFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.INVITE_USER_TO_ORGANIZATION_FAILED,
  payload: errorMessage,
});

export const resendOrganizationInvitation = (invitationId): OrganizationAction => ({
  type: ActionType.RESEND_ORGANIZATION_INVITATION,
  payload: { invitationId },
});

export const resendOrganizationInvitationSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.RESEND_ORGANIZATION_INVITATION_SUCCESS,
  payload: successMessage,
});

export const resendOrganizationInvitationFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.RESEND_ORGANIZATION_INVITATION_FAILED,
  payload: errorMessage,
});

export const cancelOrganizationInvitation = (invitationId): OrganizationAction => ({
  type: ActionType.CANCEL_ORGANIZATION_INVITATION,
  payload: { invitationId },
});

export const cancelOrganizationInvitationSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.CANCEL_ORGANIZATION_INVITATION_SUCCESS,
  payload: successMessage,
});

export const cancelOrganizationInvitationFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.CANCEL_ORGANIZATION_INVITATION_FAILED,
  payload: errorMessage,
});

export const removeUserManagerFlag = (userId): OrganizationAction => ({
  type: ActionType.REMOVE_USER_MANAGER_FLAG,
  payload: { userId },
});

export const removeUserManagerFlagSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.REMOVE_USER_MANAGER_FLAG_SUCCESS,
  payload: successMessage,
});

export const removeUserManagerFlagFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.REMOVE_USER_MANAGER_FLAG_FAILED,
  payload: errorMessage,
});

export const makeUserManager = (userId): OrganizationAction => ({
  type: ActionType.MAKE_USER_MANAGER,
  payload: { userId },
});

export const makeUserManagerSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.MAKE_USER_MANAGER_SUCCESS,
  payload: successMessage,
});

export const makeUserManagerFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.MAKE_USER_MANAGER_FAILED,
  payload: errorMessage,
});

export const associateLicense = (userId, planId): OrganizationAction => ({
  type: ActionType.ASSOCIATE_LICENSE,
  payload: { userId, planId },
});

export const associateLicenseSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.ASSOCIATE_LICENSE_SUCCESS,
  payload: successMessage,
});

export const associateLicenseFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.ASSOCIATE_LICENSE_FAILED,
  payload: errorMessage,
});

export const cancelLicense = (userId): OrganizationAction => ({
  type: ActionType.CANCEL_LICENSE,
  payload: { userId },
});

export const cancelLicenseSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.CANCEL_LICENSE_SUCCESS,
  payload: successMessage,
});

export const cancelLicenseFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.CANCEL_LICENSE_FAILED,
  payload: errorMessage,
});

export const removeUserFromOrganization = (userId): OrganizationAction => ({
  type: ActionType.REMOVE_USER_FROM_ORGANIZATION,
  payload: { userId },
});

export const removeUserFromOrganizationSuccess = (successMessage, recoveryCode = null, userId = null): OrganizationAction => ({
  type: ActionType.REMOVE_USER_FROM_ORGANIZATION_SUCCESS,
  payload: { successMessage, recoveryCode, userId },
});

export const removeUserFromOrganizationFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.REMOVE_USER_FROM_ORGANIZATION_FAILED,
  payload: errorMessage,
});

export const loadOrganizationValuePropositions = (): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_VALUE_PROPOSITIONS,
  payload: null,
});

export const loadOrganizationValuePropositionsSuccess = (vpList): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_VALUE_PROPOSITIONS_SUCCESS,
  payload: vpList,
});

export const loadOrganizationValuePropositionsFailed = (
  errorMessage: string
): OrganizationAction => ({
  type: ActionType.LOAD_ORGANIZATION_VALUE_PROPOSITIONS_FAILED,
  payload: errorMessage,
});

export const getOrganizationValuePropositionDetails = (vpId): OrganizationAction => ({
  type: ActionType.GET_ORGANIZATION_VALUE_PROPOSITION_DETAILS,
  payload: { vpId },
});

export const getOrganizationValuePropositionDetailsSuccess = (
  vpId,
  vpDetails
): OrganizationAction => ({
  type: ActionType.GET_ORGANIZATION_VALUE_PROPOSITION_DETAILS_SUCCESS,
  payload: { vpId, vpDetails },
});

export const getOrganizationValuePropositionDetailsFailed = (
  errorMessage: string
): OrganizationAction => ({
  type: ActionType.GET_ORGANIZATION_VALUE_PROPOSITION_DETAILS_FAILED,
  payload: errorMessage,
});

export const reassignValueProposition = (valuePropositionId, targetUserId): OrganizationAction => ({
  type: ActionType.REASSIGN_VALUE_PROPOSITION,
  payload: { valuePropositionId, targetUserId },
});

export const reassignValuePropositionSuccess = (message): OrganizationAction => ({
  type: ActionType.REASSIGN_VALUE_PROPOSITION_SUCCESS,
  payload: message,
});

export const reassignValuePropositionFailed = (message): OrganizationAction => ({
  type: ActionType.REASSIGN_VALUE_PROPOSITION_FAILED,
  payload: message,
});

export const reassignValuePropositions = (valuePropositionIds, targetUserId): OrganizationAction => ({
  type: ActionType.REASSIGN_VALUE_PROPOSITIONS,
  payload: { valuePropositionIds, targetUserId },
});

export const reassignValuePropositionsSuccess = (summary): OrganizationAction => ({
  type: ActionType.REASSIGN_VALUE_PROPOSITIONS_SUCCESS,
  payload: summary,
});

export const reassignValuePropositionsFailed = (summary): OrganizationAction => ({
  type: ActionType.REASSIGN_VALUE_PROPOSITIONS_FAILED,
  payload: summary,
});

export const clearReassignState = (): OrganizationAction => ({
  type: ActionType.CLEAR_REASSIGN_STATE,
  payload: null,
});

export const upgradeLicenses = (numberOfLicenses, licenseType): OrganizationAction => ({
  type: ActionType.ORGANIZATION_UPGRADE_LICENSES,
  payload: { numberOfLicenses, licenseType },
});

export const upgradeLicensesSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.ORGANIZATION_UPGRADE_LICENSES_SUCCESS,
  payload: successMessage,
});

export const upgradeLicensesFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.ORGANIZATION_UPGRADE_LICENSES_FAILED,
  payload: errorMessage,
});

export const downgradeLicenses = (numberOfLicenses, licenseType): OrganizationAction => ({
  type: ActionType.ORGANIZATION_DOWNGRADE_LICENSES,
  payload: { numberOfLicenses, licenseType },
});

export const downgradeLicensesSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.ORGANIZATION_DOWNGRADE_LICENSES_SUCCESS,
  payload: successMessage,
});

export const downgradeLicensesFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.ORGANIZATION_DOWNGRADE_LICENSES_FAILED,
  payload: errorMessage,
});

export const changeOrganizationUserLicenses = (
  numberOfLicenses,
  licenseType,
  isUpgrade,
  quantity
): OrganizationAction => ({
  type: ActionType.CHANGE_ORGANIZATION_USER_LICENSES,
  payload: { numberOfLicenses, licenseType, isUpgrade, quantity },
});

export const changeOrganizationUserLicensesSuccess = (license): OrganizationAction => ({
  type: ActionType.CHANGE_ORGANIZATION_USER_LICENSES_SUCCESS,
  payload: license,
});

export const changeOrganizationUserLicensesFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.CHANGE_ORGANIZATION_USER_LICENSES_FAILED,
  payload: errorMessage,
});

export const clearOrganizationUserLicense = () => ({
  type: ActionType.CLEAR_ORGANIZATION_USER_LICENSE,
  payload: null,
});

export const userLicenseSuccessStrip = (sessionId: ?string = null): OrganizationAction => ({
  type: ActionType.ORGANIZATION_USER_LICENSE_SUCCESS_STRIPE,
  payload: sessionId,
});

export const userLicenseSuccessStripSuccess = (successMessage): OrganizationAction => ({
  type: ActionType.ORGANIZATION_USER_LICENSE_SUCCESS_STRIPE_SUCCESS,
  payload: successMessage,
});

export const userLicenseSuccessStripFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.ORGANIZATION_USER_LICENSE_SUCCESS_STRIPE_FAILED,
  payload: errorMessage,
});

export const getMyInvitations = (): OrganizationAction => ({
  type: ActionType.GET_MY_INVITATIONS,
  payload: null,
});

export const getMyInvitationsSuccess = (invitations): OrganizationAction => ({
  type: ActionType.GET_MY_INVITATIONS_SUCCESS,
  payload: invitations,
});

export const getMyInvitationsFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.GET_MY_INVITATIONS_FAILED,
  payload: errorMessage,
});

export const acceptInvitation = (organizationId): OrganizationAction => ({
  type: ActionType.ACCEPT_INVITATION,
  payload: { organizationId },
});

export const acceptInvitationSuccess = (
  successMessage,
  newUserToken,
  organizationId
): OrganizationAction => ({
  type: ActionType.ACCEPT_INVITATION_SUCCESS,
  payload: { newUserToken, successMessage, organizationId },
});

export const acceptInvitationFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.ACCEPT_INVITATION_FAILED,
  payload: errorMessage,
});

export const rejectInvitation = (organizationId): OrganizationAction => ({
  type: ActionType.REJECT_INVITATION,
  payload: { organizationId },
});

export const rejectInvitationSuccess = (successMessage, organizationId): OrganizationAction => ({
  type: ActionType.REJECT_INVITATION_SUCCESS,
  payload: { organizationId, successMessage },
});

export const rejectInvitationFailed = (errorMessage: string): OrganizationAction => ({
  type: ActionType.REJECT_INVITATION_FAILED,
  payload: errorMessage,
});

export const acceptOrganizationInvitation = (tkn) => ({
  type: ActionType.ACCEPT_ORGANIZATION_INVITATION,
  payload: { tkn },
});

export const acceptOrganizationInvitationSuccess = (message, isSuccess) => ({
  type: ActionType.ACCEPT_ORGANIZATION_INVITATION_SUCCESS,
  payload: { message, isSuccess },
});

export const acceptOrganizationInvitationFailed = (message, isSuccess) => ({
  type: ActionType.ACCEPT_ORGANIZATION_INVITATION_FAILED,
  payload: { message, isSuccess },
});

export const loadOrganizationInvitationTokenDetails = (tkn) => ({
  type: ActionType.LOAD_ORGANIZATION_INVITATION_TOKEN_DETAILS,
  payload: { tkn },
});

export const loadOrganizationInvitationTokenDetailsSuccess = (details) => ({
  type: ActionType.LOAD_ORGANIZATION_INVITATION_TOKEN_DETAILS_SUCCESS,
  payload: details,
});

export const loadOrganizationInvitationTokenDetailsFailed = (errorMessage: string) => ({
  type: ActionType.LOAD_ORGANIZATION_INVITATION_TOKEN_DETAILS_FAILED,
  payload: errorMessage,
});

export const declineOrganizationInvitation = (tkn) => ({
  type: ActionType.DECLINE_ORGANIZATION_INVITATION,
  payload: { tkn },
});

export const declineOrganizationInvitationSuccess = (message, isSuccess) => ({
  type: ActionType.DECLINE_ORGANIZATION_INVITATION_SUCCESS,
  payload: { message, isSuccess },
});

export const declineOrganizationInvitationFailed = (message, isSuccess) => ({
  type: ActionType.DECLINE_ORGANIZATION_INVITATION_FAILED,
  payload: { message, isSuccess },
});

export const checkOwnerAndGetManagers = (): OrganizationAction => ({
  type: ActionType.CHECK_OWNER_AND_GET_MANAGERS,
  payload: null,
});

export const checkOwnerAndGetManagersSuccess = (ownerCheck): OrganizationAction => ({
  type: ActionType.CHECK_OWNER_AND_GET_MANAGERS_SUCCESS,
  payload: ownerCheck,
});

export const checkOwnerAndGetManagersFailed = (error: string): OrganizationAction => ({
  type: ActionType.CHECK_OWNER_AND_GET_MANAGERS_FAILED,
  payload: error,
});

export const transferOwnership = (transferToUserId: number): OrganizationAction => ({
  type: ActionType.TRANSFER_OWNERSHIP,
  payload: { transferToUserId },
});

export const transferOwnershipSuccess = (message: string): OrganizationAction => ({
  type: ActionType.TRANSFER_OWNERSHIP_SUCCESS,
  payload: { message },
});

export const transferOwnershipFailed = (error: string): OrganizationAction => ({
  type: ActionType.TRANSFER_OWNERSHIP_FAILED,
  payload: error,
});

export const deactivateOrganization = (): OrganizationAction => ({
  type: ActionType.DEACTIVATE_ORGANIZATION,
  payload: null,
});

export const deactivateOrganizationSuccess = (recoveryCode: string, message: string): OrganizationAction => ({
  type: ActionType.DEACTIVATE_ORGANIZATION_SUCCESS,
  payload: { recoveryCode, message },
});

export const deactivateOrganizationFailed = (error: string): OrganizationAction => ({
  type: ActionType.DEACTIVATE_ORGANIZATION_FAILED,
  payload: error,
});

export const sendRecoveryCodeEmail = (): OrganizationAction => ({
  type: ActionType.SEND_RECOVERY_CODE_EMAIL,
  payload: null,
});

export const sendRecoveryCodeEmailSuccess = (message: string): OrganizationAction => ({
  type: ActionType.SEND_RECOVERY_CODE_EMAIL_SUCCESS,
  payload: message,
});

export const sendRecoveryCodeEmailFailed = (error: string): OrganizationAction => ({
  type: ActionType.SEND_RECOVERY_CODE_EMAIL_FAILED,
  payload: error,
});

export const generateRecoveryCodeAfterUnsubscribe = (): OrganizationAction => ({
  type: ActionType.GENERATE_RECOVERY_CODE_AFTER_UNSUBSCRIBE,
  payload: null,
});

export const generateRecoveryCodeAfterUnsubscribeSuccess = (recoveryCode: string, message: string): OrganizationAction => ({
  type: ActionType.GENERATE_RECOVERY_CODE_AFTER_UNSUBSCRIBE_SUCCESS,
  payload: { recoveryCode, message },
});

export const generateRecoveryCodeAfterUnsubscribeFailed = (error: string): OrganizationAction => ({
  type: ActionType.GENERATE_RECOVERY_CODE_AFTER_UNSUBSCRIBE_FAILED,
  payload: error,
});

export const reactivateOrganization = (recoveryCode: string): OrganizationAction => ({
  type: ActionType.REACTIVATE_ORGANIZATION,
  payload: { recoveryCode },
});

export const reactivateOrganizationSuccess = (message: string, organizationId: number, organizationName: string): OrganizationAction => ({
  type: ActionType.REACTIVATE_ORGANIZATION_SUCCESS,
  payload: { message, organizationId, organizationName },
});

export const reactivateOrganizationFailed = (error: string): OrganizationAction => ({
  type: ActionType.REACTIVATE_ORGANIZATION_FAILED,
  payload: error,
});